/**
 * 69.21 step 5 (DECISIONS 241 (3), (5)): the technical as the test bed — a mount slot on the bed that a new weapon drops into,
 * and the launchers on it. The owner: "I think in both cases I'd prefer an articulating mount like the .50, except it would
 * be over the shoulder for ADM and side by side over both shoulders for laws" · "it'll actually probably be a pretty good
 * proving ground for new mounted weapons to just slap them on top of a technical for early testing".
 *
 * **The slot.** The technical's turret group (vehicleModels `technicalBed`): its origin is the gunner's feet on the bed floor,
 * +z forward, +x the gunner's right; it turns with the traverse (`turret.rotation.y = aimYaw`, `updateVehicleModel`). A weapon
 * in the slot builds its mount under it and gives back a `cradle` that pitches (`cradle.rotation.x = −aimPitch`), its rounds
 * (each one's muzzle, its back-blast end, the tube a reload swaps), the gunner's grips (none on a crewless one) and its lay's
 * limits. The .50 is today's (`technicalBed`); the 40 mm grenade launcher, later, takes the .50's cradle.
 *
 * **The shoulder yoke.** The launchers elevate about a line through the gunner's shoulders (`MOUNT_SLOT.shoulder`): the tube
 * lies on the shoulder at every lay and only the arms move, the hands on the grips (the person's IK). Two looks for the yoke
 * (`MountYoke`): A, an open fork from a post ahead of the gunner (the .50's post, its fork's arms up past the gunner's to
 * bearings at the shoulder line); B, a gooseneck from a post behind the gunner, over the head, the bearings hung from it.
 *
 * **The MLRS** (DECISIONS 241 (5): "for MLRS I think it probably won't have a human gunner … this will be the somewhat janky
 * version"): a pod bolted on a turntable mid-bed, nobody at it (`MlrsPod`: A a 12-tube block, B an aircraft rocket pod on a
 * welded frame, C two small pods on a rail).
 *
 * Person units (a person is 1.8), as the vehicle models. Presentation only; primitives, one material a colour.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { markShared } from './meshBake';
import type { UnitPalette } from './unitPalette';
import { buildLaw, setLawExtended, LAW, type LawModel } from './groundWeapons';
import { buildAntiDropshipMissile, setAntiDropshipMissileRaised, ANTI_DROPSHIP_MISSILE, type AntiDropshipMissileModel } from './antiDropshipMissileLooks';

/**
 * The owner's picks (DECISIONS 241, Oct 22: "Ok laws yoke a, ADM yoke B, MLRS A.  Ideally spare reloads are stashed in the
 * front of the bed"): the rocket technical's twin LAWs on yoke A, the missile technical's tube on yoke B; the LAWs' grips
 * not named — the lean, A (the handlebar). The MLRS's pod A is the sheet's until its armament kind is in play.
 */
export const MOUNT_PICKS = {
  rocket: { weapon: 'law2', yoke: 'A', grips: 'A' },
  missile: { weapon: 'adm', yoke: 'B' },
  mlrs: { pod: 'A' },
} as const;

/** A weapon the slot takes (the .50 is `technicalBed`'s today; 'gl' is the 40 mm's place, later). */
export type MountWeapon = 'law2' | 'adm' | 'mlrs';
/** The shoulder yoke's look: A the open fork from a post ahead, B the gooseneck from behind, over the head. */
export type MountYoke = 'A' | 'B';
/** The MLRS's pod: A the 12-tube block, B an aircraft rocket pod on a welded frame, C two small pods on a rail. */
export type MlrsPod = 'A' | 'B' | 'C';
/** The twin LAWs' grips: A a handlebar under the tubes (spade grips, a thumb paddle), B each hand on its own tube's trigger bar. */
export type LawGrips = 'A' | 'B';
export const MOUNT_YOKES: Record<MountYoke, string> = {
  A: 'the open fork: a post ahead of the gunner (the .50\'s), its arms up past the gunner\'s to the bearings at the shoulders',
  B: 'the gooseneck: a post behind the gunner, over the head, the bearings hung from it at the shoulders',
};
export const LAW_GRIPS: Record<LawGrips, string> = {
  A: 'a handlebar under the tubes ahead of the face: the .50\'s spade grips and a thumb paddle that fires each tube in turn',
  B: 'each hand on its own tube\'s trigger bar: the infantry hold doubled, both hands up by the face',
};
export const MLRS_PODS: Record<MlrsPod, string> = {
  A: 'the 12-tube block (3 × 4 in a welded frame), the field rocket launcher\'s',
  B: 'an aircraft rocket pod (19 tubes in a round pod) strapped to an angle-iron frame — the janky one',
  C: 'two small aircraft pods side by side on a rail',
};

/**
 * The slot's measures, in the turret's frame (the gunner's feet): `shoulder` the line the launchers elevate about (the tube's
 * rest on the shoulder: `x` either side of the neck, `y` over the feet in the hatch pose, `z` ahead of the feet), `bearing`
 * how far out the yoke's bearings stand (clear of the upper arms), `pitch` each weapon's lay (radians: down, up), `mlrsZ`
 * where the MLRS's turntable stands ahead of the slot's origin (mid-bed: its pod's back clear of the tailgate).
 */
export const MOUNT_SLOT = {
  shoulder: { x: 0.25, y: 1.5, z: 0.02 },
  bearing: 0.5,
  pitch: { law2: [-0.17, 0.8], adm: [-0.1, 1.15], mlrs: [0, 0.95] } as Record<MountWeapon, readonly [number, number]>,
  mlrsZ: 0.75,
};

/** A round the mount fires: its muzzle, the end its back blast leaves (none on a closed pod's), the tube a reload swaps (none on a pod's). */
export interface MountRound { muzzle: THREE.Object3D; rear?: THREE.Object3D; tube?: THREE.Group }
export interface MountBuild {
  weapon: MountWeapon;
  /** pitched by the lay: rotation.x = −pitch (as `updateVehicleModel` pitches a cradle) */
  cradle: THREE.Group;
  /** the yoke or the turntable: turns with the turret, does not pitch */
  base: THREE.Group;
  rounds: MountRound[];
  /** the gunner's grips (the person's hands on them); none: crewless */
  hands?: { right: THREE.Object3D; left: THREE.Object3D };
  pitch: readonly [number, number];
  /** the launcher models on the cradle (the LAWs, the missile's tube): their sights and covers stand up when they are fresh */
  launchers: Array<LawModel | AntiDropshipMissileModel>;
}

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string): THREE.MeshLambertMaterial {
  let m = matCache.get(color);
  if (!m) { m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true })); matCache.set(color, m); }
  return m;
}
function box(parent: THREE.Object3D, w: number, h: number, d: number, color: string, x: number, y: number, z: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat(color));
  m.position.set(x, y, z); parent.add(m);
  return m;
}
/** A cylinder along `axis` (its centre at x, y, z). */
function rod(parent: THREE.Object3D, r0: number, r1: number, len: number, color: string, x: number, y: number, z: number, axis: 'x' | 'y' | 'z', seg = 10): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.CylinderGeometry(r0, r1, len, seg), mat(color));
  if (axis === 'x') m.rotation.z = Math.PI / 2; else if (axis === 'z') m.rotation.x = Math.PI / 2;
  m.position.set(x, y, z); parent.add(m);
  return m;
}
/** A bar from a to b (a box `t` thick). */
function bar(parent: THREE.Object3D, a: THREE.Vector3, b: THREE.Vector3, t: number, color: string): THREE.Mesh {
  const d = new THREE.Vector3().subVectors(b, a), len = d.length();
  const m = new THREE.Mesh(new THREE.BoxGeometry(t, t, len), mat(color));
  m.position.copy(a).addScaledVector(d, 0.5);
  m.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), d.normalize());
  parent.add(m);
  return m;
}
const anchor = (parent: THREE.Object3D, x: number, y: number, z: number): THREE.Object3D => { const o = new THREE.Object3D(); o.position.set(x, y, z); parent.add(o); return o; };
const v = (x: number, y: number, z: number) => new THREE.Vector3(x, y, z);

/**
 * The yoke under `turret` with bearings on `sides` (−1 left, +1 right) at the shoulder line: A the fork from a post ahead,
 * B the gooseneck from behind. The bearings' housings are the yoke's; the cradle's hubs turn in them.
 */
function yoke(turret: THREE.Group, p: UnitPalette, look: MountYoke, sides: number[]): THREE.Group {
  const g = new THREE.Group(); g.name = 'yoke'; turret.add(g);
  const S = MOUNT_SLOT, by = S.shoulder.y, bz = S.shoulder.z, bx = S.bearing, metal = p.metal;
  if (look === 'A') {
    // the post ahead of the gunner (where the .50's stands), a crossbar at its head, a fork arm a side up and back to its bearing
    const postZ = 0.5, top = 1.0;
    box(g, 0.36, 0.04, 0.36, metal, 0, 0.02, postZ);
    rod(g, 0.05, 0.07, top, metal, 0, top / 2, postZ, 'y', 8).userData.thin = true;
    rod(g, 0.08, 0.08, 0.1, p.dark, 0, top - 0.02, postZ, 'y', 10);   // the traverse's collar
    const w = sides.length === 2 ? bx : bx / 2 + 0.05;
    box(g, sides.length === 2 ? 2 * bx + 0.05 : w + 0.05, 0.06, 0.06, metal, sides.length === 2 ? 0 : sides[0] * w / 2, top + 0.03, postZ);
    for (const s of sides) bar(g, v(s * bx, top + 0.03, postZ), v(s * bx, by, bz), 0.055, metal);
  } else {
    // the gooseneck: a post behind the gunner up past the head, forward over it, across, and a hanger down each side to its bearing
    const postZ = -0.5, top = 2.02;
    box(g, 0.36, 0.04, 0.3, metal, 0, 0.02, postZ);
    rod(g, 0.055, 0.075, top, metal, 0, top / 2, postZ, 'y', 8).userData.thin = true;
    bar(g, v(0, top, postZ), v(0, top, bz), 0.06, metal);                                    // forward over the head
    const x0 = Math.min(...sides) * bx, x1 = Math.max(...sides) * bx;
    box(g, x1 - x0 + 0.06 + (sides.length === 1 ? bx : 0), 0.06, 0.06, metal, sides.length === 1 ? sides[0] * bx / 2 : 0, top, bz);   // across
    for (const s of sides) bar(g, v(s * bx, top, bz), v(s * bx, by, bz), 0.055, metal);   // the hangers
    box(g, 0.2, 0.18, 0.03, p.dark, 0, 1.42, postZ + 0.06);   // the blast plate on the post (the tubes' back blast)
  }
  for (const s of sides) rod(g, 0.06, 0.06, 0.08, p.dark, s * (bx + 0.02), by, bz, 'x', 12);   // the bearings
  return g;
}

/**
 * The cradle at the shoulder line: a hub a side in the bearings, a bracket from each forward to a clamp ring round its tube.
 * The tubes lie at ±`shoulder.x` (`sides`), their rest 0.12 behind their middle (the person's slot: HAND_POSE.rocket).
 */
function shoulderCradle(turret: THREE.Group, p: UnitPalette, sides: number[], radius: number): THREE.Group {
  const S = MOUNT_SLOT, c = new THREE.Group(); c.name = 'cradle';
  c.position.set(0, S.shoulder.y, S.shoulder.z); turret.add(c);
  const ringZ = 0.34, bx = S.bearing - 0.04;
  for (const s of sides) {
    const tx = s * S.shoulder.x;
    rod(c, radius + 0.022, radius + 0.022, 0.07, p.dark, tx, 0, ringZ, 'z', 12);              // the clamp ring
    box(c, 0.03, 0.03, 0.05, p.dark, tx, radius + 0.03, ringZ);                                  // its screw
    bar(c, v(tx + s * (radius + 0.02), 0, ringZ), v(s * bx, 0, ringZ), 0.045, p.metal);         // out to the side
    bar(c, v(s * bx, 0, ringZ), v(s * bx, 0, 0), 0.045, p.metal);                                // back to the hub
    rod(c, 0.04, 0.04, 0.07, p.metal, s * (bx + 0.02), 0, 0, 'x', 10);                          // the hub
  }
  return c;
}

/** The twin LAWs side by side over both shoulders, on a handlebar (the .50's spade grips: a thumb paddle between). */
function lawPair(turret: THREE.Group, p: UnitPalette, look: MountYoke, gripLook: LawGrips): MountBuild {
  const base = yoke(turret, p, look, [-1, 1]);
  const cradle = shoulderCradle(turret, p, [-1, 1], LAW.radius);
  const S = MOUNT_SLOT, rounds: MountRound[] = [], launchers: LawModel[] = [];
  for (const s of [-1, 1]) {
    const law = buildLaw(p); setLawExtended(law, 1);
    const tube = new THREE.Group(); tube.name = `tube ${s < 0 ? 'left' : 'right'}`; cradle.add(tube);
    tube.position.set(s * S.shoulder.x, 0, 0.12); tube.add(law.group);
    const rear = anchor(law.inner, 0, 0, -LAW.length / 2 - 0.02);
    rounds.push({ muzzle: law.muzzle, rear, tube });
    launchers.push(law);
  }
  if (gripLook === 'B') return { weapon: 'law2', cradle, base, rounds, hands: { left: launchers[0].gripR, right: launchers[1].gripR }, pitch: S.pitch.law2, launchers };
  // the handlebar under the tubes ahead of the face: a drop from each clamp, a bar across, the spade grips back to the hands
  const hz = 0.36, hy = -0.16;
  for (const s of [-1, 1]) box(cradle, 0.035, -hy - LAW.radius, 0.035, p.dark, s * S.shoulder.x, (hy - LAW.radius) / 2, hz - 0.02);
  box(cradle, 2 * S.shoulder.x + 0.04, 0.035, 0.035, p.dark, 0, hy, hz);
  const grips: THREE.Object3D[] = [];
  for (const s of [-1, 1]) {
    const gx = s * 0.15;
    const spade = box(cradle, 0.032, 0.13, 0.032, p.dark, gx, hy - 0.035, hz - 0.07); spade.rotation.x = 0.5;
    grips.push(anchor(cradle, gx, hy - 0.04, hz - 0.08));
  }
  box(cradle, 0.06, 0.02, 0.03, p.flash, 0, hy - 0.02, hz - 0.05);   // the thumb paddle (each fires its tube in turn)
  return { weapon: 'law2', cradle, base, rounds, hands: { left: grips[0], right: grips[1] }, pitch: S.pitch.law2, launchers };
}

/**
 * The anti-dropship missile over the right shoulder on a one-sided yoke: the right hand on its own trigger grip, the left on a
 * fore-grip on the clamp, inboard of the tube (the missile's own fore-grip is 0.58 ahead of its middle: the infantry blade the
 * torso to reach it; the gunner stands square in the bed, and it would be 0.9 off the left shoulder, past the arm's reach).
 */
function admMount(turret: THREE.Group, p: UnitPalette, look: MountYoke): MountBuild {
  const base = yoke(turret, p, look, [1]);
  const A = ANTI_DROPSHIP_MISSILE, S = MOUNT_SLOT;
  const cradle = shoulderCradle(turret, p, [1], A.radius);
  const m = buildAntiDropshipMissile(p, undefined, { bake: false }); setAntiDropshipMissileRaised(m, 1);
  const tube = new THREE.Group(); tube.name = 'tube right'; cradle.add(tube);
  tube.position.set(S.shoulder.x, 0, A.shoulder); tube.add(m.group);
  const fx = 0.06, fy = -0.13, fz = 0.33;
  bar(cradle, v(S.shoulder.x - A.radius - 0.02, -0.02, 0.34), v(fx, fy + 0.07, 0.34), 0.035, p.dark);   // the fore-grip's arm off the clamp
  const fore = box(cradle, 0.035, 0.13, 0.035, p.dark, fx, fy, fz); fore.rotation.x = 0.35;
  return { weapon: 'adm', cradle, base, rounds: [{ muzzle: m.muzzle, rear: m.breech, tube }], hands: { right: m.gripR, left: anchor(cradle, fx, fy, fz) }, pitch: S.pitch.adm, launchers: [m] };
}

/** The MLRS: a turntable mid-bed (`MOUNT_SLOT.mlrsZ` ahead of the slot), cheeks, the pod's cradle pivoted at its back. Crewless. */
function mlrsMount(turret: THREE.Group, p: UnitPalette, pod: MlrsPod): MountBuild {
  const base = new THREE.Group(); base.name = 'turntable'; turret.add(base);
  base.position.z = MOUNT_SLOT.mlrsZ;
  rod(base, 0.5, 0.52, 0.08, p.metal, 0, 0.04, 0, 'y', 16);
  for (const s of [-1, 1]) { const ch = box(base, 0.05, 0.5, 0.55, p.metal, s * 0.42, 0.3, -0.12); ch.userData.thin = true; }
  rod(base, 0.05, 0.05, 0.9, p.dark, 0, 0.5, -0.3, 'x', 10);   // the trunnion across the cheeks
  // the elevating screw from the turntable up to the cradle's belly (it stretches as it lays: posed by the sheet, a strut)
  const cradle = new THREE.Group(); cradle.name = 'cradle'; cradle.position.set(0, 0.5, -0.3); base.add(cradle);
  const rounds: MountRound[] = [];
  const tubeAt = (x: number, y: number, z: number, r: number, len: number, colour: string) => {
    rod(cradle, r, r, len, colour, x, y, z, 'z', 10);
    rod(cradle, r * 0.72, r * 0.72, 0.012, '#141414', x, y, z + len / 2 + 0.002, 'z', 10);   // the bore's dark mouth
    rounds.push({ muzzle: anchor(cradle, x, y, z + len / 2 + 0.01) });
  };
  if (pod === 'A') {
    // 3 rows × 4 tubes in a welded box frame
    const r = 0.055, len = 1.0, z = 0.42, y0 = 0.16;
    for (let row = 0; row < 3; row++) for (let col = 0; col < 4; col++) tubeAt((col - 1.5) * 0.13, y0 + row * 0.12, z, r, len, p.body);
    for (const zz of [z - 0.42, z + 0.4]) { box(cradle, 0.6, 0.03, 0.04, p.metal, 0, y0 - 0.075, zz); box(cradle, 0.6, 0.03, 0.04, p.metal, 0, y0 + 0.315, zz); for (const s of [-1, 1]) box(cradle, 0.03, 0.42, 0.04, p.metal, s * 0.3, y0 + 0.12, zz); }
    box(cradle, 0.5, 0.04, 0.9, p.metal, 0, y0 - 0.1, z);   // the bed it is bolted to
  } else if (pod === 'B') {
    // a round pod (19 tubes) with its nose cone, two straps to an angle-iron frame
    const R = 0.24, len = 1.45, y = 0.3, z = 0.5;
    rod(cradle, R, R, len, '#7b7f78', 0, y, z, 'z', 16);
    const nose = new THREE.Mesh(new THREE.CylinderGeometry(R, R * 0.35, 0.3, 16), mat('#6c706a')); nose.rotation.x = -Math.PI / 2; nose.position.set(0, y, z - len / 2 - 0.15); cradle.add(nose);
    rounds.length = 0;
    const holes: Array<[number, number]> = [[0, 0]];
    for (let i = 0; i < 6; i++) holes.push([0.09 * Math.cos(i * Math.PI / 3), 0.09 * Math.sin(i * Math.PI / 3)]);
    for (let i = 0; i < 12; i++) holes.push([0.18 * Math.cos(i * Math.PI / 6 + 0.26), 0.18 * Math.sin(i * Math.PI / 6 + 0.26)]);
    for (const [hx, hy] of holes) { rod(cradle, 0.035, 0.035, 0.012, '#141414', hx, y + hy, z + len / 2 + 0.002, 'z', 8); rounds.push({ muzzle: anchor(cradle, hx, y + hy, z + len / 2 + 0.01) }); }
    for (const zz of [z - 0.35, z + 0.35]) rod(cradle, R + 0.012, R + 0.012, 0.05, p.dark, 0, y, zz, 'z', 16);   // the straps
    for (const s of [-1, 1]) box(cradle, 0.05, 0.05, 1.0, p.metal, s * 0.2, y - R - 0.02, z);   // the angle iron
    for (const zz of [z - 0.35, z + 0.35]) box(cradle, 0.45, 0.04, 0.05, p.metal, 0, y - R - 0.02, zz);
  } else {
    // two small pods (7 tubes each) side by side on a rail
    const R = 0.15, len = 1.15, y = 0.24, z = 0.45;
    for (const s of [-1, 1]) {
      const x = s * 0.2;
      rod(cradle, R, R, len, '#7b7f78', x, y, z, 'z', 14);
      const nose = new THREE.Mesh(new THREE.CylinderGeometry(R, R * 0.35, 0.22, 14), mat('#6c706a')); nose.rotation.x = -Math.PI / 2; nose.position.set(x, y, z - len / 2 - 0.11); cradle.add(nose);
      const holes: Array<[number, number]> = [[0, 0]];
      for (let i = 0; i < 6; i++) holes.push([0.085 * Math.cos(i * Math.PI / 3), 0.085 * Math.sin(i * Math.PI / 3)]);
      for (const [hx, hy] of holes) { rod(cradle, 0.032, 0.032, 0.012, '#141414', x + hx, y + hy, z + len / 2 + 0.002, 'z', 8); rounds.push({ muzzle: anchor(cradle, x + hx, y + hy, z + len / 2 + 0.01) }); }
      rod(cradle, R + 0.01, R + 0.01, 0.04, p.dark, x, y, z, 'z', 14);
    }
    box(cradle, 0.62, 0.05, 0.9, p.metal, 0, y - 0.16, z);   // the rail
  }
  return { weapon: 'mlrs', cradle, base, rounds, pitch: MOUNT_SLOT.pitch.mlrs, launchers: [] };
}

/**
 * The weapon `weapon` in the technical's slot `turret` (its origin the gunner's feet). The launchers on the shoulder yoke
 * (`look`), the MLRS on its turntable (`pod`).
 */
export function buildMount(turret: THREE.Group, p: UnitPalette, weapon: MountWeapon, opts: { yoke?: MountYoke; pod?: MlrsPod; grips?: LawGrips } = {}): MountBuild {
  if (weapon === 'law2') return lawPair(turret, p, opts.yoke ?? 'A', opts.grips ?? 'A');
  if (weapon === 'adm') return admMount(turret, p, opts.yoke ?? 'A');
  return mlrsMount(turret, p, opts.pod ?? 'A');
}

/** Lay the mount: its cradle's pitch, clamped to the weapon's (the turret's yaw is `updateVehicleModel`'s). */
export function layMount(b: MountBuild, pitch: number): void {
  b.cradle.rotation.x = -Math.max(b.pitch[0], Math.min(b.pitch[1], pitch));
}


// ---- in play: the spares' rack at the front of the bed, and the reload drawn from the seat's state ------------------------

/** The rack's measures: its slots (the store's most, MOUNT_STORE_RANGE's 6), rows across the bed against the cab's back wall, two deep. */
export const BED_RACK = { slots: 6, rows: 3, deep: 2, gap: 0.03 };
export interface BedRack {
  /** one mesh, the slots' tubes merged in the order they are taken last (slot 0 the bottom front): its draw range shows the first `n` */
  mesh: THREE.Mesh;
  /** each slot's tube middle and turn (the tube along the bed's x), in the rack's frame */
  slots: THREE.Object3D[];
  /** vertices a slot (the draw range's step) */
  per: number;
}
/** A spare tube lying in the rack: its body, the band at its front and its caps, a colour each (vertex colours: one draw). */
function spareTubeGeometry(weapon: 'law2' | 'adm'): THREE.BufferGeometry {
  const law = weapon === 'law2';
  const r = law ? LAW.radius : ANTI_DROPSHIP_MISSILE.radius, len = law ? LAW.length : ANTI_DROPSHIP_MISSILE.length;
  const body = law ? LAW.color : '#686d6e', band = law ? LAW.band : '#3a3e40', cap = law ? LAW.cap : '#3a3e40';
  const part = (g: THREE.BufferGeometry, colour: string): THREE.BufferGeometry => {
    const n = g.index ? g.toNonIndexed() : g, c = new THREE.Color(colour), cols = new Float32Array(n.getAttribute('position').count * 3);
    for (let i = 0; i < cols.length; i += 3) { cols[i] = c.r; cols[i + 1] = c.g; cols[i + 2] = c.b; }
    n.setAttribute('color', new THREE.BufferAttribute(cols, 3));
    n.deleteAttribute('uv');
    return n;
  };
  const tube = new THREE.CylinderGeometry(r, r, len, 10).rotateX(Math.PI / 2);
  const ring = new THREE.CylinderGeometry(r + 0.004, r + 0.004, 0.045, 10).rotateX(Math.PI / 2).translate(0, 0, len / 2 - 0.1);
  const front = new THREE.CylinderGeometry(r + 0.005, r + 0.005, 0.02, 10).rotateX(Math.PI / 2).translate(0, 0, len / 2 + 0.01);
  const back = new THREE.CylinderGeometry(r + 0.005, r + 0.005, 0.02, 10).rotateX(Math.PI / 2).translate(0, 0, -len / 2 - 0.01);
  return mergeGeometries([part(tube, body), part(ring, band), part(front, cap), part(back, cap)], false)!;
}
/**
 * The spare reloads stowed at the front of the bed (the owner: "Ideally spare reloads are stashed in the front of the bed"):
 * up to `BED_RACK.slots` tubes lying across the bed against the cab's back wall, three high, two deep, between two end frames.
 * One draw for the tubes: the store's count shows as the first `n` slots (`showRack`), emptying from the top as the gunner
 * reloads. `at`: the bed's floor, the cab's back wall (z) and the bed's half width, in `parent`'s frame.
 */
export function bedRack(parent: THREE.Group, p: UnitPalette, weapon: 'law2' | 'adm', at: { floor: number; front: number; half: number }): BedRack {
  const law = weapon === 'law2', r = (law ? LAW.radius : ANTI_DROPSHIP_MISSILE.radius) + 0.006, R = BED_RACK;
  const g = new THREE.Group(); g.name = 'rack'; parent.add(g);
  const one = spareTubeGeometry(weapon), per = one.getAttribute('position').count, geos: THREE.BufferGeometry[] = [], slots: THREE.Object3D[] = [];
  const pitch = 2 * r + R.gap + 0.02;
  for (let i = 0; i < R.slots; i++) {
    const row = Math.floor(i / R.deep), col = i % R.deep;
    const y = at.floor + 0.05 + r + row * (2 * r + R.gap), z = at.front - 0.08 - r - col * pitch;
    const slot = anchor(g, 0, y, z); slot.rotation.y = Math.PI / 2; slots.push(slot);
    geos.push(one.clone().rotateY(Math.PI / 2).translate(0, y, z));
  }
  const mesh = new THREE.Mesh(mergeGeometries(geos, false)!, markShared(new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));
  mesh.name = 'rack tubes'; mesh.userData.noBake = true; g.add(mesh);   // (its draw range is the store's count: never merged)
  // the frame: an end plate a side, a strap over the top
  const depth = R.deep * pitch + 0.04, zMid = at.front - 0.06 - (R.deep * pitch) / 2, top = at.floor + 0.05 + R.rows * (2 * r + R.gap);
  const len = law ? LAW.length : ANTI_DROPSHIP_MISSILE.length;
  for (const s of [-1, 1]) box(g, 0.04, top - at.floor, depth, p.metal, s * Math.min(at.half - 0.06, len / 2 - 0.12), (top + at.floor) / 2, zMid);
  box(g, Math.min(2 * at.half - 0.1, len - 0.2), 0.03, depth + 0.02, p.dark, 0, top + 0.015, zMid).name = 'rack strap';
  return { mesh, slots, per };
}
/**
 * A tube's meshes collapsed into one (vertex colours: one draw), its anchors (muzzle, grips, back-blast end) kept where they
 * are: a tube that moves on its own through a reload costs a draw, not one a colour. Kept out of the vehicle's bake.
 */
export function collapseTube(tube: THREE.Group): THREE.Mesh {
  tube.updateMatrixWorld(true);
  const inv = new THREE.Matrix4().copy(tube.matrixWorld).invert(), geos: THREE.BufferGeometry[] = [], meshes: THREE.Mesh[] = [];
  tube.traverse(o => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || Array.isArray(m.material) || !(m.material instanceof THREE.MeshLambertMaterial)) return;
    const g0 = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone();
    for (const k of Object.keys(g0.attributes)) if (k !== 'position' && k !== 'normal') g0.deleteAttribute(k);
    g0.applyMatrix4(new THREE.Matrix4().multiplyMatrices(inv, m.matrixWorld));
    const c = m.material.color, cols = new Float32Array(g0.getAttribute('position').count * 3);
    for (let i = 0; i < cols.length; i += 3) { cols[i] = c.r; cols[i + 1] = c.g; cols[i + 2] = c.b; }
    g0.setAttribute('color', new THREE.BufferAttribute(cols, 3));
    geos.push(g0); meshes.push(m);
  });
  for (const m of meshes) m.parent?.remove(m);
  const one = new THREE.Mesh(mergeGeometries(geos, false)!, TUBE_MAT());
  one.name = 'tube (one draw)'; one.userData.noBake = true; tube.add(one);
  return one;
}
let tubeMat: THREE.MeshLambertMaterial | null = null;
const TUBE_MAT = () => (tubeMat ??= markShared(new THREE.MeshLambertMaterial({ vertexColors: true, flatShading: true })));

/** The rack showing `n` spares (the store's count: the first `n` slots). */
export function showRack(rack: BedRack, n: number): void {
  const k = Math.max(0, Math.min(rack.slots.length, Math.floor(n)));
  rack.mesh.geometry.setDrawRange(0, k * rack.per);
  rack.mesh.visible = k > 0;
}

/**
 * A mount in play: its build, the rack, each tube's rest (its place in the cradle), the gunner's hands (an object the host's
 * rider reads each frame: `hands.right` / `hands.left` re-pointed through the reload), the root the toss falls to (its y = 0
 * the ground) and where the near hand takes each tube.
 */
export interface MountInPlay {
  b: MountBuild;
  rack: BedRack;
  rest: Array<{ pos: THREE.Vector3; quat: THREE.Quaternion }>;
  hands: { right: THREE.Object3D; left: THREE.Object3D };
  /**
   * the store when last no reload was under way: the sim takes a reload's tubes out of the store as it starts (and a dry mount
   * starts it the tick after its last shot, so a frame may never see it dry) — the store's drop is the tubes it swaps
   */
  storeWas?: number;
  root: THREE.Object3D;
  holds: THREE.Object3D[];
}
export function mountInPlay(b: MountBuild, rack: BedRack, root: THREE.Object3D): MountInPlay {
  const rest = b.rounds.map(r => ({ pos: r.tube!.position.clone(), quat: r.tube!.quaternion.clone() }));
  const holds = b.rounds.map(r => anchor(r.tube!, 0, -0.04, 0.1));
  return { b, rack, rest, hands: { right: b.hands!.right, left: b.hands!.left }, root, holds };
}

/** The reload's beats, in a tube's share of the reload: the spent tube slides back out of its clamp, is tossed over its side; the rest, the fresh one from the rack. */
export const MOUNT_RELOAD_BEATS = { slide: 0.18, toss: 0.22 };
const _w = new THREE.Vector3(), _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _s = new THREE.Vector3(), _x = new THREE.Vector3(), _e = new THREE.Euler();
const easeIn = (k: number) => k * k * (3 - 2 * k);
/**
 * Pose a mount from its seat's state: `loaded` the tubes the sim counts loaded (`belt`), `store` the spares (`spare`), `reload`
 * how far through a reload (0–1; absent: none) and `seconds` the reload's length. The rack shows the store and, through a
 * reload, the tubes not yet taken; each tube the reload refills (those spent, in turn) slides back out of its clamp, is tossed
 * over its side and falls to the ground, and a fresh one comes from the rack's top slot to the clamp, the near hand on it (the
 * other on its grip). Reads the state, writes only the drawing. The root's matrices must be current.
 */
export function poseMount(v: MountInPlay, st: { loaded: number; store: number; reload?: number; seconds: number }): void {
  const { b } = v, n = b.rounds.length;
  for (let i = 0; i < n; i++) { const t = b.rounds[i].tube!; t.position.copy(v.rest[i].pos); t.quaternion.copy(v.rest[i].quat); }
  v.hands.right = b.hands!.right; v.hands.left = b.hands!.left;
  if (st.reload === undefined) { v.storeWas = st.store; showRack(v.rack, st.store); return; }
  // the tubes this reload swaps: as many as it took from the store, the last loaded (the tubes spend from the last loaded)
  const take = Math.max(0, Math.min(st.loaded, (v.storeWas ?? st.store) - st.store)), swaps: number[] = [];
  for (let i = st.loaded - take; i < st.loaded; i++) swaps.push(i);
  if (!swaps.length) { showRack(v.rack, st.store); return; }
  const share = 1 / swaps.length, k = Math.max(0, Math.min(1 - 1e-9, st.reload)), j = Math.floor(k / share), u = (k - j * share) / share;
  const i = swaps[j], tube = b.rounds[i].tube!, side = b.weapon === 'adm' ? 1 : Math.sign(v.rest[i].pos.x) || 1;
  const B = MOUNT_RELOAD_BEATS, fetching = u >= B.slide + B.toss;
  // the rack: the store, and the tubes still to come out of it (this one too until its fetch begins)
  showRack(v.rack, st.store + (swaps.length - j - (fetching ? 1 : 0)));
  const near = (o: THREE.Object3D) => { if (side < 0) v.hands.left = o; else v.hands.right = o; };
  if (u < B.slide) {
    tube.position.z = v.rest[i].pos.z - 0.55 * easeIn(u / B.slide);
    near(v.holds[i]);
    return;
  }
  b.cradle.updateWorldMatrix(true, false);
  if (!fetching) {
    // tossed over its side: out and up from the clamp, falling to the ground (the root's y = 0), turning as it goes
    const secs = (u - B.slide) * share * st.seconds;
    const p = b.cradle.localToWorld(_w.set(v.rest[i].pos.x, v.rest[i].pos.y, v.rest[i].pos.z - 0.55));
    v.root.worldToLocal(p);
    _x.set(1, 0, 0).applyQuaternion(b.cradle.parent!.getWorldQuaternion(_q)).applyQuaternion(v.root.getWorldQuaternion(_q2).invert());
    p.addScaledVector(_x, side * 2.0 * secs);
    p.y = Math.max(0.07, p.y + 2.2 * secs - 4.9 * secs * secs);
    b.cradle.worldToLocal(v.root.localToWorld(p));
    tube.position.copy(p);
    tube.quaternion.copy(v.rest[i].quat).multiply(_q.setFromEuler(_e.set(2.4 * secs, 0, side * 3.2 * secs)));
    return;
  }
  // the fresh one from the rack's top slot to the clamp, lifted over the rail on the way, the near hand on it
  const f = easeIn((u - B.slide - B.toss) / (1 - B.slide - B.toss));
  const slot = v.rack.slots[Math.max(0, Math.min(v.rack.slots.length - 1, st.store + (swaps.length - j) - 1))];
  slot.updateWorldMatrix(true, false); slot.matrixWorld.decompose(_w, _q, _s);
  b.cradle.getWorldQuaternion(_q2);
  const start = b.cradle.worldToLocal(_w), startQ = _q2.invert().multiply(_q);
  tube.position.lerpVectors(start, v.rest[i].pos, f);
  tube.position.y += 0.35 * Math.sin(Math.PI * f);
  tube.quaternion.slerpQuaternions(startQ, v.rest[i].quat, f);
  near(v.holds[i]);
}

/** The muzzle of the tube a rocket just left (the tubes spend from the last loaded: the one at `loadedAfter`, the count after the shot). */
export function firedMuzzle(v: MountInPlay, loadedAfter: number): THREE.Object3D {
  return v.b.rounds[Math.max(0, Math.min(v.b.rounds.length - 1, loadedAfter))].muzzle;
}

